'use client';

import { type CSSProperties, useLayoutEffect, useState } from 'react';

import type { OutcomeColorClasses } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';

// Why no rAF chain: `node.scrollWidth` reads from inside a
// `requestAnimationFrame` callback force a synchronous layout — rAF runs after
// style invalidation but before the next layout pass, so the browser must
// re-compute layout to answer the query. A 3-deep chain (mount + ResizeObserver
// + fonts-ready, each scheduling 3 measurements) compounded into the dominant
// app-side forced-reflow in the /demo-mode trace (47 ms in
// `MarketRowOutcomeLabelButton.measure`, 3 ms in this hook). ResizeObserver
// callbacks fire AFTER layout is clean, so a single measurement inside the RO
// callback is layout-safe. Label-change re-measurement happens inside
// `useLayoutEffect`, which is also post-commit / pre-paint.

/** Tooltip uses `--outcome-*-tooltip-surface`: same hue weight as bar fill in
 *  dark mode, but composited on `--background-primary` so the panel is opaque
 *  (spec 068 `fill` is 25% + transparent and must not be used on portaled
 *  tooltips — underlying text shows through). */
export function outcomeLabelTooltipClassName(c: OutcomeColorClasses | undefined): string {
  return cn(
    'max-w-[min(90vw,20rem)] border-0 px-3 py-1.5 text-body font-medium text-balance shadow-elevation-popover rounded-control',
    c === undefined
      ? 'bg-background-tertiary text-foreground-secondary'
      : cn(`bg-outcome-${c.hue}-tooltip-surface`, c.text),
  );
}

/** SVG polygon fill matches chip; see `globals.css` `[data-slot="tooltip-content"] svg polygon`. */
export function outcomeLabelTooltipContentStyle(c: OutcomeColorClasses | undefined): CSSProperties {
  return {
    '--tooltip-caret-fill':
      c === undefined ? 'var(--background-tertiary)' : `var(--outcome-${c.hue}-tooltip-surface)`,
  } as CSSProperties;
}

export function outcomeLabelTooltipArrowClassName(c: OutcomeColorClasses | undefined): string {
  return c === undefined ? 'bg-background-tertiary' : `bg-outcome-${c.hue}-tooltip-surface`;
}

/**
 * Measures whether a single-line `truncate` span is actually clipped. Returns
 * a ref-callback for the span plus the current truncation state. Re-measures on
 * `label` change, container resize, and after web-font loading.
 *
 * Pair with `<AppTooltip disabled={!isTruncated}>` and the
 * `outcomeLabelTooltip*` styling helpers so long outcome labels get a
 * hue-tinted tooltip only when they overflow.
 */
export function useOutcomeLabelTruncation(label: string): {
  setLabelSpanEl: (node: HTMLSpanElement | null) => void;
  isTruncated: boolean;
} {
  const [spanEl, setLabelSpanEl] = useState<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (spanEl === null) return;
    const measure = (): void => {
      if (spanEl.isConnected === false || spanEl.textContent !== label) return;
      const next = spanEl.scrollWidth > spanEl.clientWidth + 1;
      setIsTruncated((prev) => (prev === next ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(spanEl);
    const parent = spanEl.parentElement;
    if (parent !== null) {
      ro.observe(parent);
    }
    const { fonts } = document;
    if (fonts !== undefined) {
      void fonts.ready.then(() => {
        measure();
      });
    }
    return (): void => {
      ro.disconnect();
    };
  }, [label, spanEl]);

  return { setLabelSpanEl, isTruncated };
}
